import { Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { AuthGuard } from '@/components/auth/AuthGuard';
import { PlatformLoginPage } from '@/modules/platform/pages/PlatformLoginPage';
import { PlatformForgotPasswordPage } from '@/modules/platform/pages/auth/PlatformForgotPasswordPage';
import { PlatformResetPasswordCheckEmailPage } from '@/modules/platform/pages/auth/PlatformResetPasswordCheckEmailPage';
import { PlatformResetPasswordPage } from '@/modules/platform/pages/auth/PlatformResetPasswordPage';
import { PlatformResetPasswordChangedPage } from '@/modules/platform/pages/auth/PlatformResetPasswordChangedPage';
import { PlatformDashboard } from '@/modules/platform/pages/PlatformDashboard';
import { DealersPage } from '@/modules/platform/pages/DealersPage';
import { TenantsListPage } from '@/modules/platform/pages/TenantsListPage';
import { TenantDetailPage } from '@/modules/platform/pages/TenantDetailPage';
import { ProfilePage } from '@/modules/platform/pages/profile/ProfilePage';
import { UsersListPage } from '@/modules/platform/pages/users/UsersListPage';
import { PlatformAiSettingsPage } from '@/modules/platform/pages/ai-settings/PlatformAiSettingsPage';
import { PlatformSettingsPage } from '@/modules/platform/pages/settings/PlatformSettingsPage';
import { useAuth } from '@/hooks/useAuth';
import { NotFoundPage } from '@/modules/platform/pages/NotFoundPage';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '@marine/ui/card';

/**
 * Platform routes - only loaded when accessing the main domain
 * (e.g., http://marine-dms.local/dashboard)
 */
export function PlatformRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background px-6 py-10">
        <Card className="w-full max-w-sm">
          <CardContent className="p-6">
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
              <div className="text-sm font-medium">Loading platform</div>
              <div className="text-xs text-muted-foreground">Checking your session and permissions.</div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <Routes>
      {/* Platform admin login */}
      <Route
        path="/login"
        element={
          !isAuthenticated ? (
            <PlatformLoginPage />
          ) : (
            <Navigate to="/dashboard" replace />
          )
        }
      />

      {/* Password reset (public, no auth) */}
      <Route path="/forgot-password" element={<PlatformForgotPasswordPage />} />
      <Route path="/reset-password/check-email" element={<PlatformResetPasswordCheckEmailPage />} />
      <Route path="/reset-password" element={<PlatformResetPasswordPage />} />
      <Route path="/reset-password/changed" element={<PlatformResetPasswordChangedPage />} />

      {/* Protected platform admin routes */}
      <Route
        path="/"
        element={
          <AuthGuard>
            <Layout />
          </AuthGuard>
        }
      >
        {/* Platform routes */}
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<PlatformDashboard />} />
        <Route path="dealers/*" element={<DealersPage />} />
        <Route path="platform/tenants" element={<TenantsListPage />} />
        <Route path="platform/tenants/:id" element={<TenantDetailPage />} />

        {/* AI Settings (Phase 39+) — platform-wide provider keys */}
        <Route path="platform/ai-settings" element={<PlatformAiSettingsPage />} />

        {/* Platform Settings (#824) — platform-managed third-party client keys */}
        <Route path="platform/settings" element={<PlatformSettingsPage />} />

        {/* User Management */}
        <Route path="users" element={<UsersListPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
